import styled from "@emotion/styled";
import {
  Box,
  FlexRow,
  H2,
  IconWarningSign,
  Link,
  NoBreak,
  Stack,
  Text,
} from "atoms";
import { GenericImage } from "features/images";
import { formatDate } from "utils/date";
import type { OpenCallSubmission } from "../types";

type OpenCallSubmissionSummaryProps = Pick<
  OpenCallSubmission,
  | "submissionId"
  | "submission"
  | "title"
  | "name"
  | "isEligible"
  | "openCallId"
  | "createdByUserId"
  | "createdAt"
> & {
  userIsSubmitter: boolean;
  isSubmissionsOpen: boolean;
  openCallId: string;
  openCallTitle?: string;
  // display options
  showArtistName: boolean;
  showOpenCallLink: boolean;
  showCreatedAt: boolean;
  // Twitter handle, including the '@' symbol
  twitterHandle?: string;
  isSuccessPage?: boolean;
};

function OpenCallSubmissionSummary({
  createdAt,
  isEligible = false,
  isSubmissionsOpen = true,
  name,
  twitterHandle,
  openCallId,
  openCallTitle,
  showArtistName = false,
  showOpenCallLink = true,
  showCreatedAt = true,
  isSuccessPage = false,
  submission,
  submissionId,
  title,
  userIsSubmitter,
}: OpenCallSubmissionSummaryProps) {
  if (!submissionId) {
    return null;
  }
  const showEditLink = isSubmissionsOpen && userIsSubmitter;
  // If showing any metadata, we need to prefix it
  const showMetadataPrefix =
    showArtistName ||
    (showOpenCallLink && !!openCallTitle) ||
    (showCreatedAt && !!createdAt);

  // Tweet text for users
  let tweetText = `Look what I found!%0A%0AI was exploring submissions for ${
    openCallTitle ? `the ${openCallTitle}` : `an`
  } Open Call on @thehugxyz and found "${title}" by ${
    twitterHandle ?? name
  }. Check it out on HUG 🤗`;

  // tweet text for the artist...
  if (userIsSubmitter) {
    tweetText = `I just submitted ${
      isSuccessPage ? "my artwork" : `"${title}"`
    } to ${openCallTitle ?? "an Open Call"} on @thehugxyz! ${
      isSuccessPage ? "Wish me luck," : "Check it out"
    } and let me know what you think!`;
  }

  const submissionLink = `/open-calls/${openCallId}/submissions/${submissionId}`;
  const tweetURL = `hug.art${submissionLink}`;
  const tweetLink = `https://twitter.com/intent/tweet?text=${tweetText}%0A${tweetURL}`;

  return (
    <SBox>
      <Stack gap="xs">
        <FlexRow flexWrap="nowrap" itemsFlex="">
          <div
            style={{ flex: "0 0 80px", alignSelf: "flex-start" }}
            aria-hidden
          >
            <Link to={submissionLink}>
              <GenericImage src={submission} sizes="80px" width="80px" />
            </Link>
          </div>
          <Stack gap="xs">
            <Title size="sm" textTransform="none">
              <Link to={submissionLink}>{title}</Link>
            </Title>
            <Stack gap="xxs">
              <IneligibleWarning
                userIsArtist={userIsSubmitter}
                isEligible={isEligible}
              />
              <SText size="xxs">
                <FlexRow gap="4px" as="span">
                  {showMetadataPrefix && <span>Submitted</span>}
                  {(showOpenCallLink && !!openCallTitle) ||
                    showArtistName ||
                    (!!createdAt && showCreatedAt)}
                  {showOpenCallLink && openCallTitle && (
                    <span>
                      to{" "}
                      <Link to={`/open-calls/${openCallId}`}>
                        {openCallTitle}
                      </Link>
                    </span>
                  )}
                  {showArtistName && <span> by {name}</span>}
                  {!!createdAt && showCreatedAt && (
                    <span>
                      {" "}
                      on{" "}
                      <NoBreak>
                        {formatDate(createdAt, {
                          dateStyle: "medium",
                        })}
                      </NoBreak>
                    </span>
                  )}
                </FlexRow>
              </SText>

              <FlexRow gap="4px .66rem">
                <Link size="xxs" to={submissionLink}>
                  View
                </Link>
                {showEditLink && (
                  <Link size="xxs" to={`${submissionLink}/edit`}>
                    Edit
                  </Link>
                )}
                {showOpenCallLink && !openCallTitle && (
                  <Link size="xxs" to={`/open-calls/${openCallId}`}>
                    Visit Open Call
                  </Link>
                )}
                {userIsSubmitter && (
                  <Link size="xxs" target="_blank" href={tweetLink}>
                    Share on Twitter
                  </Link>
                )}
              </FlexRow>
            </Stack>
          </Stack>
        </FlexRow>
      </Stack>
    </SBox>
  );
}

function IneligibleWarning({
  userIsArtist,
  isEligible,
}: {
  userIsArtist: boolean;
  isEligible: boolean;
}) {
  if (!userIsArtist || isEligible) {
    return null;
  }
  return (
    <RedFlexRow flexWrap="nowrap" itemsFlex="" gap="0 4px">
      <SIconWarningSign />
      <Text size="xxxs" bold>
        You must{" "}
        <Link to="/artist-application">apply for an Artist Profile</Link> to be
        eligible for selection
      </Text>
    </RedFlexRow>
  );
}

const SBox = styled(Box)(({ theme }) => ({
  padding: "1rem",
  lineHeight: 1.2,
  strong: {
    color: theme.colors.fg,
  },
}));

const Title = styled(H2)({
  a: { textDecoration: "none" },
  "a:hover, a:focus-visible": {
    textDecoration: "underline",
  },
});

const SText = styled(Text)({
  marginBottom: 0,
});

const RedFlexRow = styled(FlexRow)(({ theme }) => ({
  "&,a": { color: theme.colors.accent4 },
  p: { margin: 0 },
}));

const SIconWarningSign = styled(IconWarningSign)(({ theme }) => ({
  flex: "0 0 .9em",
  alignSelf: "flex-start",
  color: theme.colors.accent4,
}));

export { OpenCallSubmissionSummary };
